import { Component } from 'react';
import styled from 'styled-components';
import { Button } from '../Button/Button';
import { ButtonToolbar } from '../ButtonToolbar/ButtonToolbar';
import { LinkButton } from '../LinkButton/LinkButton';
import { IStep, Stepper } from '../Stepper/Stepper';

const WizardViewElement = styled.div`
  display: grid;
  grid-template-areas: 'content' 'footer';
  grid-template-rows: 1fr auto;
  height: 100%;
`;

const ContentElement = styled.div`
  grid-area: content;
  display: flex;
  flex: 1;
  flex-direction: column;
  padding: ${(props: any) => props.padding || 0}px;
  overflow: auto;
` as any;

const WizardFooterElement = styled.div`
  grid-area: footer;
  display: flex;
  align-items: center;
  padding: 0px 40px;
  border-top: ${props => `1px solid ${props.theme.vars.newColor.lightGS1}`};
  z-index: 8;
  height: 55px;
`;

const LeftElement = styled.div`
  display: flex;
  justify-content: flex-start;
  flex: 1;
`;
const MiddleElement = styled.div`
  display: flex;
  justify-content: center;
  flex: 1;
`;

const RightElement = styled.div`
  display: flex;
  justify-content: flex-end;
  flex: 1;
`;

const StepperElement = styled.div`
  & > * {
    justify-content: center;
  }
`;

export interface IProps {
  /** The current step which starts at 1. Highlights the current step in the Stepper. */
  currentStep: number;
  /** A list of all steps.  Currently, IStep has only one "label" field. */
  steps: IStep[];
  /** Event handler fired when previous button is clicked. */
  onPrevButtonClicked?: () => void;
  /** Event handler fired when next button is clicked. */
  onNextButtonClicked?: () => void;
  /** Event handler fired when previous button is clicked. */
  onSkipButtonClicked?: () => void;
  /** Event handler fired when exit button is clicked. */
  onExitButtonClicked?: () => void;
  /** Event handler fired when one of the steps is clicked. */
  onStepClicked?: () => void;
  /** The padding applied to the content of the WizardView */
  padding?: number;
  /** Label for previous button. Default to "Back" */
  prevButtonLabel?: string;
  /** Label for next button. Default to "Next" */
  nextButtonLabel?: string;
  /** Disable the prev button. Defaults to false. */
  prevButtonDisabled?: boolean;
  /** Label for previous button. Default to "Skip step" */
  skipButtonLabel?: string;
  /** Label for exit button. Default to "Cancel & Exit" */
  exitButtonLabel?: string;
  /** Disable the next button. Defaults to false. */
  nextButtonDisabled?: boolean;
  /** Disable the skip button. Defaults to false. */
  skipButtonDisabled?: boolean;
  /** Disable the exit button. Defaults to false. */
  exitButtonDisabled?: boolean;
}

export class WizardView extends Component<IProps> {
  public render() {
    return (
      <WizardViewElement data-testid="WizardView">
        <ContentElement padding={this.props.padding}>{this.props.children}</ContentElement>
        <WizardFooterElement>
          <LeftElement />

          <MiddleElement>
            <StepperElement>
              <Stepper
                currentStep={this.props.currentStep}
                steps={this.props.steps}
                selectable={this.props.onStepClicked != null}
                onChange={this.props.onStepClicked}
              />
            </StepperElement>
          </MiddleElement>

          <RightElement>
            <ButtonToolbar>
              {this.props.onExitButtonClicked && (
                <LinkButton onClick={this.props.onExitButtonClicked} disabled={this.props.exitButtonDisabled}>
                  {this.props.exitButtonLabel || 'Cancel & Exit'}
                </LinkButton>
              )}

              {this.props.onSkipButtonClicked && (
                <LinkButton onClick={this.props.onSkipButtonClicked} disabled={this.props.skipButtonDisabled}>
                  {this.props.skipButtonLabel || 'Skip'}
                </LinkButton>
              )}

              {this.props.onPrevButtonClicked && (
                <Button onClick={this.props.onPrevButtonClicked} disabled={this.props.prevButtonDisabled}>
                  {this.props.prevButtonLabel || 'Back'}
                </Button>
              )}

              {this.props.onNextButtonClicked && (
                <Button
                  variation="primary"
                  disabled={this.props.nextButtonDisabled}
                  onClick={this.props.onNextButtonClicked}
                >
                  {this.props.nextButtonLabel ?? 'Next'}
                </Button>
              )}
            </ButtonToolbar>
          </RightElement>
        </WizardFooterElement>
      </WizardViewElement>
    );
  }
}
